import { useEffect, useRef, useState } from 'react'
import { humanSessionAPI } from '../../services/humanSessionAPI'
import { useVoiceDictation } from '../../hooks/useVoiceDictation'

// ── small pieces ─────────────────────────────────────────────────────────────

function verdictClass(v) {
  return v === 'aligned' ? 'text-success' : v === 'partial' ? 'text-warning' : 'text-error'
}
function scoreBarColor(s) {
  if (s == null) return 'bg-base-300'
  if (s >= 0.8) return 'bg-success'
  if (s >= 0.4) return 'bg-warning'
  return 'bg-error'
}

function PhaseScore({ label, score }) {
  if (!score) return null
  const s = score.score_mean
  return (
    <div className="text-xs">
      <div className="flex justify-between">
        <span className="font-semibold">{label}</span>
        <span className={`font-mono ${verdictClass(score.verdict_mean)}`}>{score.verdict_mean} · {s?.toFixed(2)}</span>
      </div>
      <div className="h-1.5 bg-base-200 rounded mt-0.5 overflow-hidden">
        <div className={`h-full ${scoreBarColor(s)}`} style={{ width: `${Math.round((s || 0) * 100)}%` }} />
      </div>
    </div>
  )
}

function Turn({ speaker, text }) {
  const isClinician = speaker === 'clinician' || speaker === 'you'
  return (
    <div className={`text-sm px-3 py-1.5 rounded ${isClinician ? 'bg-primary/8 border border-primary/20' : 'bg-base-200'}`}>
      <div className="text-[10px] uppercase tracking-wide text-base-content/60 font-mono">
        {isClinician ? (speaker === 'you' ? 'You' : 'Clinician') : 'Patient'}
      </div>
      <div className="whitespace-pre-wrap">{text}</div>
    </div>
  )
}

// Voice/text input for the trainee's question
function ClinicianInput({ onAsk, disabled, useWhisper }) {
  const { isSupported, isListening, transcript, setTranscript, interimText, startListening, stopListening, reset } =
    useVoiceDictation({ useWhisper, whisperUploadFn: useWhisper ? humanSessionAPI.transcribeAudio : null })
  const ask = () => {
    const text = transcript.trim()
    if (!text) return
    onAsk(text); reset()
  }
  return (
    <div className="space-y-2">
      <textarea
        className="textarea textarea-bordered w-full text-sm resize-none"
        rows={2}
        placeholder="Ask the patient…"
        value={transcript + (interimText ? ' ' + interimText : '')}
        onChange={(e) => setTranscript(e.target.value)}
        disabled={disabled}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask() } }}
      />
      <div className="flex gap-2 items-center">
        {isSupported && (
          <button className={`btn btn-xs ${isListening ? 'btn-error' : 'btn-ghost border border-base-300'}`}
            onClick={() => (isListening ? stopListening() : startListening())} disabled={disabled}>
            {isListening ? <span className="flex items-center gap-1"><span className="loading loading-ring loading-xs" />Stop</span>
              : transcript.trim() ? '🎤 Add' : '🎤 Speak'}
          </button>
        )}
        <button className="btn btn-primary btn-xs" onClick={ask} disabled={disabled || !transcript.trim()}>Ask →</button>
      </div>
    </div>
  )
}

function PhaseHeader({ phase }) {
  return (
    <div className="mb-3">
      <div className="text-xs uppercase tracking-wide text-base-content/60 font-mono">
        Phase {phase.phase_number} of {phase.total_phases} · {phase.month_label}
      </div>
      <h3 className="text-base font-semibold mt-0.5">{phase.setting}</h3>
      {phase.reveal_to_clinician && (
        <div className="alert alert-info py-1.5 mt-2 text-xs">
          <span><span className="font-semibold">Objective data this visit:</span> {phase.reveal_to_clinician}</span>
        </div>
      )}
      <div className="text-xs text-base-content/60 mt-1">
        <span className="font-semibold">Clinical goal:</span> {phase.expected_action}
      </div>
    </div>
  )
}

// A completed phase: full two-column reveal + coaching.
function CompletedPhase({ record }) {
  const { phase, humanTurns, humanAssessment, phaseScore, agentEncounter, agentPhaseScore, coaching } = record
  return (
    <div className="card bg-base-100 border border-base-300 p-4">
      <PhaseHeader phase={phase} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* You */}
        <div className="rounded border border-primary/20 p-3">
          <div className="text-xs font-semibold text-primary mb-2">🩺 You</div>
          <div className="space-y-1.5 mb-2">
            {humanTurns.map((t, i) => <Turn key={i} speaker={t.speaker} text={t.text} />)}
          </div>
          <div className="text-sm bg-base-100 border-t border-base-200 pt-2">
            <div className="text-[10px] uppercase text-base-content/60 font-mono mb-1">Your assessment</div>
            <div className="whitespace-pre-wrap">{humanAssessment}</div>
          </div>
          <div className="mt-2"><PhaseScore label="Your score" score={phaseScore} /></div>
        </div>
        {/* Agent */}
        <div className="rounded border border-base-300 p-3">
          <div className="text-xs font-semibold mb-2">🤖 Agent</div>
          {agentEncounter ? (
            <>
              <div className="space-y-1.5 mb-2">
                {(agentEncounter.dialogue || []).filter((t) => t.text).map((t, i) => <Turn key={i} speaker={t.speaker} text={t.text} />)}
              </div>
              <div className="text-sm border-t border-base-200 pt-2">
                <div className="text-[10px] uppercase text-base-content/60 font-mono mb-1">Agent assessment</div>
                <div className="whitespace-pre-wrap">{agentEncounter.assessment_text}</div>
                {agentEncounter.assessment_citations?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {agentEncounter.assessment_citations.map((c) => <span key={c} className="badge badge-ghost badge-xs font-mono">{c}</span>)}
                  </div>
                )}
              </div>
              <div className="mt-2"><PhaseScore label="Agent score" score={agentPhaseScore} /></div>
            </>
          ) : <div className="text-xs text-base-content/50">Agent reveal unavailable.</div>}
        </div>
      </div>

      {/* Coaching */}
      {coaching && !coaching.error && (coaching.summary || coaching.improvements?.length) && (
        <div className="mt-3 border-t border-base-200 pt-3 space-y-2">
          {coaching.summary && <div className="alert py-2 text-sm"><span>{coaching.summary}</span></div>}
          {coaching.improvements?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">Where to improve</div>
              <ul className="list-disc list-inside text-sm space-y-0.5">{coaching.improvements.map((q, i) => <li key={i}>{q}</li>)}</ul>
            </div>
          )}
          {coaching.agent_examples?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">What the agent did well</div>
              <ul className="list-disc list-inside text-sm space-y-0.5 text-base-content/80">{coaching.agent_examples.map((q, i) => <li key={i}>{q}</li>)}</ul>
            </div>
          )}
          {coaching.missed?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-warning mb-1">You missed</div>
              <ul className="list-disc list-inside text-sm space-y-0.5">{coaching.missed.map((q, i) => <li key={i}>{q}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── main ─────────────────────────────────────────────────────────────────────

export default function SideBySideTraining({ caseInfo, providers = ['openai'], simConfig, useWhisper = false }) {
  const caseId = caseInfo?.case_id

  const [sessionId, setSessionId] = useState(null)
  const [phase, setPhase] = useState(null)          // active phase metadata
  const [turns, setTurns] = useState([])            // active phase conversation
  const [assessment, setAssessment] = useState('')
  const [completed, setCompleted] = useState([])    // CompletedPhase records
  const [summary, setSummary] = useState(null)
  const [busy, setBusy] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState(null)
  const bottomRef = useRef(null)

  useEffect(() => {
    setSessionId(null); setPhase(null); setTurns([]); setAssessment('')
    setCompleted([]); setSummary(null); setError(null)
  }, [caseId])

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [completed.length, phase])

  const start = async () => {
    setStarting(true); setError(null)
    try {
      const d = await humanSessionAPI.start({ caseId, behaviorConfig: simConfig, providers })
      setSessionId(d.session_id)
      setPhase(d.current_phase)
      setCompleted([]); setSummary(null); setTurns([]); setAssessment('')
    } catch (e) {
      setError(e?.response?.data?.detail || e.message)
    } finally {
      setStarting(false)
    }
  }

  const ask = async (text) => {
    setTurns((t) => [...t, { speaker: 'you', text }])
    setBusy(true); setError(null)
    try {
      const d = await humanSessionAPI.patientReply(sessionId, { humanUtterance: text, turnType: turns.length === 0 ? 'open' : 'followup' })
      setTurns((t) => [...t, { speaker: 'patient', text: d.patient_text }])
    } catch (e) {
      setError(e?.response?.data?.detail || e.message)
    } finally {
      setBusy(false)
    }
  }

  const submitPhase = async () => {
    if (busy) return  // avoid double-submit while a request is in flight
    setBusy(true); setError(null)
    const activePhase = phase
    const humanTurns = [...turns]
    const humanAssessment = assessment.trim() || '(no assessment provided)'
    try {
      const d = await humanSessionAPI.submitAssessment(sessionId, { humanAssessment, providers })
      setCompleted((c) => [...c, {
        phase: activePhase,
        humanTurns,
        humanAssessment,
        phaseScore: d.phase_score,
        agentEncounter: d.agent_encounter,
        agentPhaseScore: d.agent_phase_score,
        coaching: d.coaching,
      }])
      setTurns([]); setAssessment('')
      if (d.is_complete) {
        setPhase(null)
        try {
          const s = await humanSessionAPI.getSummary(sessionId, providers)
          setSummary(s)
        } catch { /* summary is best-effort */ }
      } else {
        setPhase(d.next_phase)
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message)
    } finally {
      setBusy(false)
    }
  }

  if (!caseInfo) return null

  return (
    <div className="space-y-4">
      {/* Case header */}
      <div className="card bg-base-100 border border-base-300 p-4">
        <div className="text-xs uppercase tracking-wide text-base-content/60 font-mono">Side-by-side training · {caseInfo.case_id}</div>
        <h2 className="text-xl font-bold mt-1">{caseInfo.name?.replace(/Patient \d+\w? — /, '') || caseInfo.case_id}</h2>
        <p className="text-sm text-base-content/70 mt-1">{caseInfo.one_liner}</p>
        <p className="text-xs text-base-content/60 mt-2">
          Interview the simulated patient phase by phase. After you submit each phase, the agent's independent
          interview and both scores are revealed side-by-side with coaching. Adjust patient behavior on the left.
        </p>
        {!sessionId && (
          <button className="btn btn-primary btn-sm mt-3 self-start" onClick={start} disabled={starting || !caseId}>
            {starting && <span className="loading loading-spinner loading-xs" />}
            {starting ? 'Starting…' : 'Start training session'}
          </button>
        )}
      </div>

      {error && <div className="alert alert-error text-sm">{String(error)}</div>}

      {/* Completed phases */}
      {completed.map((rec, i) => <CompletedPhase key={i} record={rec} />)}

      {/* Active phase */}
      {phase && (
        <div className="card bg-base-100 border border-primary/30 p-4">
          <PhaseHeader phase={phase} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* You */}
            <div className="rounded border border-primary/20 p-3">
              <div className="text-xs font-semibold text-primary mb-2">🩺 You</div>
              <div className="space-y-1.5 mb-2 max-h-72 overflow-y-auto">
                {turns.map((t, i) => <Turn key={i} speaker={t.speaker} text={t.text} />)}
                {busy && <div className="flex items-center gap-2 text-xs text-base-content/60"><span className="loading loading-dots loading-xs" /> …</div>}
              </div>
              <ClinicianInput onAsk={ask} disabled={busy} useWhisper={useWhisper} />
              <div className="divider text-[10px] text-base-content/50 my-2">submit your assessment for this visit</div>
              <textarea
                className="textarea textarea-bordered w-full text-sm resize-none"
                rows={3}
                placeholder="Your assessment & plan for this visit…"
                value={assessment}
                onChange={(e) => setAssessment(e.target.value)}
                disabled={busy}
              />
              <button className="btn btn-primary btn-sm mt-2 w-full" onClick={submitPhase}>
                {busy ? <span className="loading loading-spinner loading-xs" /> : null}
                Submit phase & reveal agent
              </button>
            </div>
            {/* Agent (locked) */}
            <div className="rounded border border-base-300 border-dashed p-3 flex items-center justify-center text-center">
              <div className="text-sm text-base-content/50">
                🔒 The agent's independent interview and scores for this visit unlock once you submit your assessment.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Final summary */}
      {summary && (
        <div className="card bg-base-100 border border-success/40 p-4">
          <h3 className="font-semibold text-success mb-2">Session complete</h3>
          <p className="text-sm text-base-content/70">
            You completed all {completed.length} phases. Ground-truth diagnosis:{' '}
            <span className="font-semibold">{summary?.evaluation?.ground_truth?.diagnosis || caseInfo.one_liner}</span>.
            Review the per-phase coaching above to see where your interviewing and care can improve.
          </p>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  )
}
